"use client";

import { motion } from 'framer-motion';
import { Link } from "@/i18n/navigation";
import { useState } from 'react';
import { useParams } from 'next/navigation';
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { CustomButton } from "@/components/ui/custom-button";
import { CustomForm, type CustomFormId } from "@/components/forms";
import { BenefitsRow } from "@/components/shared/benefits_row";
import { FAQ } from "@/components/ui/faq";
import { get__pilot_faq_data, PilotFAQItem } from "@/cms/pilot-faq-data";
import { ReactNode } from 'react';
import { Check } from 'lucide-react';

interface PilotProgramPageProps {
  variant: 'default' | 'cwm' | 'eab' | 'educause' | 'nl';
  title: ReactNode;
  subtitle: string;
  caseStudyLink: string;
  caseStudyImage: string;
  caseStudyAlt: string;
  reportImage: string;
  reportAlt: string;
  whyPilotTitle: string;
  institutionType: string;
  requirements: {
    title: string;
    description: string;
  }[];
  whyPilotContent?: string;
  showFAQ?: boolean;
  partnershipHeading?: string;
  collaborationSubheading?: string;
  caseStudyLinkText?: string;
  reportHeading?: string;
  reportLinkText?: string;
  applyNowText?: string;
  learnMoreText?: string;
  additionalContent?: {
    heading: string;
    paragraph: string;
    benefits: string[];
    additionalSections?: {
      heading: string;
      paragraph: string;
      image?: string;
      imageAlt?: string;
    }[];
    unlockSection?: {
      heading: string;
      paragraph: string;
      image: string;
      imageAlt: string;
    };
    pilotSection?: {
      heading: string;
      paragraph: string;
      image: string;
      imageAlt: string;
    };
    benefitsRow?: {
      heading: string;
      description: string;
      cards: {
        title: string;
        bulletPoints: {
          icon: string;
          text: string;
        }[];
        image: string;
        imageAlt: string;
        bgColor: string;
      }[];
    };
  };
}

export function PilotProgramPage({
  variant,
  title,
  subtitle,
  caseStudyLink,
  caseStudyImage,
  caseStudyAlt,
  reportImage,
  reportAlt,
  whyPilotTitle,
  institutionType,
  requirements,
  whyPilotContent,
  showFAQ = true,
  partnershipHeading = "A Partnership to Power Change",
  collaborationSubheading = "Here's what we need for a successful collaboration:",
  caseStudyLinkText = "Hear the Full Story →",
  reportHeading = "Read the EAB Case-Study",
  reportLinkText = "Read the Report →",
  applyNowText = "Apply Now",
  learnMoreText = "Learn More",
  additionalContent
}: PilotProgramPageProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Get the current locale from Next.js params
  const params = useParams();
  const locale = params?.locale as string || 'en-gb';
  
  const report_form_id: CustomFormId = variant === "cwm"
    ? "d1006-download-jcc-evaluation"
    : (variant === "eab" || variant === "educause")
      ? "e1002-eab-evaluation-paper-usa"
      : "e1000-uob-evaluation-paper";

  const application_form_id: CustomFormId = variant === "cwm"
    ? "p1002-interested-in-pilot-colleges"
    : variant === "eab"
      ? "p1004-interested-in-pilot-eab-connected25"
      : variant === "educause"
        ? "p1003-interested-in-pilot-educause25"
        : "p1001-interested-in-pilot";

  return (
    <main className="flex min-h-screen flex-col justify-center" role="main" aria-label="Pilot Program">
      {/* Hero Section with gradient background */}
      <section className="grid h-auto md:h-[475px] w-full grid-cols-1" aria-label="Pilot Program introduction">
        <div className="flex flex-col justify-center bg-ss-blue from-10% via-ss-light-blue via-50% to-ss-blue to-90% text-white px-6 py-12 md:py-16 h-full">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-4xl text-center">
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="text-3xl sm:text-4xl md:text-5xl font-sans font-medium text-white mb-6"
              >
                {title}
              </motion.h1>

              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
                className="mt-4 max-w-4xl mx-auto text-base md:text-lg font-sans font-normal text-white"
              >
                {subtitle}
              </motion.p>
              
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
                className="mt-8 flex flex-col sm:flex-row justify-center gap-4"
              >
                <CustomButton
                  onClick={() => {
                    document.getElementById('apply-now')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  variant="primary"
                  size="lg"
                >
                  {applyNowText}
                </CustomButton>
                <CustomButton
                  onClick={() => {
                    document.getElementById('learn-more')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  variant="ghost"
                  size="lg"
                >
                  {learnMoreText}
                </CustomButton>
              </motion.div>
            </div>
          </div>
        </div>
      </section>


      {/* Additional Content Section */}
      {additionalContent && (
        <section className="bg-gray-50 py-16">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              viewport={{ once: true }}
              className="text-center mb-8 sm:mb-10 md:mb-12"
            >
              <h3 className="text-xl sm:text-2xl font-lora font-bold text-black mb-4 sm:mb-6">
                {additionalContent.heading}
              </h3>
              <p className="text-sm sm:text-base md:text-lg mb-8 sm:mb-10 md:mb-12 font-sans font-normal text-black max-w-4xl mx-auto">
                {additionalContent.paragraph}
              </p>
              
              {/* Benefits Row Component */}
              {additionalContent.benefitsRow && (
                <BenefitsRow
                  heading={additionalContent.benefitsRow.heading}
                  description={additionalContent.benefitsRow.description}
                  cards={additionalContent.benefitsRow.cards}
                />
              )}
            </motion.div>
          </div>
        </section>
      )}

      {/* Unlock Exclusive Access Section */}
      {additionalContent && additionalContent.unlockSection && (
        <section className="bg-gray-50 pt-0 pb-16">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              viewport={{ once: true }}
              className="max-w-6xl mx-auto"
            >
              <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
                {/* Text Content */}
                <div className="flex-1">
                  <h3 className="text-xl sm:text-2xl font-lora font-bold text-black mb-4 sm:mb-6">
                    {additionalContent.unlockSection.heading}
                  </h3>
                  <p className="text-sm sm:text-base md:text-lg font-sans font-normal text-black leading-relaxed">
                    {additionalContent.unlockSection.paragraph}
                  </p>
                </div>
                
                {/* Image Content */}
                <div className="flex-1">
                  <div className="relative overflow-hidden rounded-3xl shadow-lg">
                    <img
                      src={additionalContent.unlockSection.image}
                      alt={additionalContent.unlockSection.imageAlt}
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* A Pilot that Puts You First Section */}
      {additionalContent && additionalContent.pilotSection && (
        <section className="bg-gray-50 py-16">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              viewport={{ once: true }}
              className="max-w-6xl mx-auto"
            >
              <div className="flex flex-col lg:flex-row-reverse items-center gap-8 lg:gap-12">
                {/* Text Content */}
                <div className="flex-1">
                  <h3 className="text-xl sm:text-2xl font-lora font-bold text-black mb-4 sm:mb-6">
                    {additionalContent.pilotSection.heading}
                  </h3>
                  <p className="text-sm sm:text-base md:text-lg font-sans font-normal text-gray-700 leading-relaxed">
                    {additionalContent.pilotSection.paragraph}
                  </p>
                </div>
                
                {/* Image Content */}
                <div className="flex-1">
                  <div className="relative overflow-hidden rounded-3xl shadow-lg">
                    <img
                      src={additionalContent.pilotSection.image}
                      alt={additionalContent.pilotSection.imageAlt}
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Why a Pilot Program Section */}
      <section id="learn-more" className="bg-white pt-16 pb-8 sm:pt-20 sm:pb-12 md:pt-24 md:pb-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            viewport={{ once: true }}
            className="mx-auto max-w-6xl"
          >
            <div className="text-center mb-8 sm:mb-10 md:mb-12">
              <h3 className="text-xl sm:text-2xl md:text-3xl font-lora font-bold text-black mb-4">
                {partnershipHeading}
              </h3>
              <p className="text-sm sm:text-base md:text-lg font-sans font-normal text-black max-w-2xl mx-auto">
                {collaborationSubheading}
              </p>
            </div>

            {/* Requirements Grid */}
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              viewport={{ once: true, margin: "-100px" }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-8 sm:mb-10 md:mb-12"
            >
              {requirements.map((req, index) => (
                <div
                  key={index}
                  className="bg-[#004C87] text-white p-8 shadow-lg h-[240px] flex flex-col relative overflow-hidden group transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 rounded-3xl"
                >
                  <div className="flex items-center mb-4 relative z-10 h-16">
                    <div className="flex items-center justify-center min-w-14 min-h-14 rounded-full bg-ss-indigo mr-4">
                      <span className="text-2xl font-bold text-white">{String(index + 1).padStart(2, '0')}</span>
                    </div>
                    <h4 className="text-2xl font-bold tracking-tight font-lora text-white">{req.title}</h4>
                  </div>
                  <p className="text-white leading-relaxed font-sans font-normal mt-2">
                    {req.description}
                  </p>
                </div>
              ))}
            </motion.div>

            <h3 className="text-xl sm:text-2xl md:text-3xl font-lora font-bold text-black text-center mt-16 sm:mt-20 md:mt-24 mb-6 sm:mb-8 md:mb-10">
              {whyPilotTitle}
            </h3>
            <div className="mx-auto max-w-6xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8 md:items-stretch">
                {/* Left Side - Case Study */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  viewport={{ once: true }}
                  className="flex"
                >
                  <Link href={caseStudyLink} className="group block w-full">
                    <div className="bg-ss-green p-4 sm:p-6 md:p-8 shadow-lg h-full flex flex-col text-center relative rounded-3xl transition-transform duration-300 hover:-translate-y-2">
                      <div className="flex flex-col flex-1 justify-center">
                        <div className="mb-3 sm:mb-4">
                          <img 
                            src={caseStudyImage}
                            alt={caseStudyAlt}
                            className="w-full h-auto rounded-3xl object-cover"
                            loading="lazy"
                            width={600}
                            height={400}
                          />
                        </div>

                        <h3 className="text-2xl font-sans font-medium text-white mb-4 min-h-14 flex items-center justify-center">
                          {whyPilotContent || "Hear it from the Students and Faculty"}
                        </h3>

                        <span className="text-white font-medium group-hover:underline">
                          {caseStudyLinkText}
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>

                {/* Right Side - Full Report */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 }}
                  viewport={{ once: true }}
                  className="flex"
                >
                  <button 
                    onClick={() => setIsModalOpen(true)} 
                    className="group w-full text-left focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-3xl"
                    aria-haspopup="dialog"
                    aria-expanded={isModalOpen}
                    aria-label="Read the full pilot evaluation report"
                  >
                    <div className="bg-ss-green p-4 sm:p-6 md:p-8 shadow-lg h-full flex flex-col text-center rounded-3xl transition-transform duration-300 hover:-translate-y-2">
                      <div className="flex flex-col flex-1 justify-center">
                        <div className="mb-3 sm:mb-4">
                          <img 
                            src={reportImage}
                            alt={reportAlt}
                            className="w-full h-auto rounded-3xl object-cover"
                            loading="lazy"
                            width={600}
                            height={400}
                          />
                        </div>

                        <h3 className="text-2xl font-sans font-medium text-white mb-4 min-h-14 flex items-center justify-center">
                          {reportHeading}
                        </h3>
                        
                        <span className="text-white font-medium group-hover:underline">
                          {reportLinkText}
                        </span>
                      </div>
                    </div>
                  </button>
                </motion.div>

                {/* Report Modal */}
                <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                  <DialogContent className="sm:max-w-[600px] max-h-[95vh] overflow-y-auto">
                    <div className="mt-6">
                      <CustomForm formId={report_form_id} />
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Additional Content Section - Additional Sections */}
      {additionalContent && additionalContent.additionalSections && (
        <section className="bg-white pt-16">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="text-center mb-8 sm:mb-10 md:mb-12">
              {additionalContent.additionalSections.map((section, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="mt-12 max-w-6xl mx-auto"
                >
                  <div className={`flex flex-col lg:flex-row items-center gap-8 lg:gap-12 ${
                    index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                  }`}>
                    {/* Text Content */}
                    <div className="flex-1">
                      <h3 className="text-xl sm:text-2xl font-lora font-bold text-black mb-4 sm:mb-6">
                        {section.heading}
                      </h3>
                      <p className="text-sm sm:text-base md:text-lg font-sans font-normal text-gray-700 leading-relaxed">
                        {section.paragraph}
                      </p>
                      
                      {/* Benefits row - only show after the second section (index 1) */}
                      {index === 1 && additionalContent.benefits && (
                        <div className="mt-8">
                          <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 justify-center items-center">
                            {additionalContent.benefits.map((benefit, benefitIndex) => (
                              <div
                                key={benefitIndex}
                                className="flex items-center gap-3 bg-white px-4 py-3 rounded-lg shadow-sm border border-gray-200"
                              >
                                <div className="shrink-0">
                                  <div className="flex items-center justify-center size-6 rounded-full bg-ss-light-green">
                                    <svg 
                                      className="size-4 text-white stroke-3" 
                                      fill="none" 
                                      viewBox="0 0 24 24" 
                                      stroke="currentColor"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                  </div>
                                </div>
                                <span className="text-base font-medium text-gray-900 whitespace-nowrap">{benefit}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Image Content */}
                    {section.image && (
                      <div className="flex-1">
                        <div className="relative overflow-hidden rounded-3xl shadow-lg">
                          <img
                            src={section.image}
                            alt={section.imageAlt || section.heading}
                            className="w-full h-auto object-cover"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}


      {/* FAQ Section */}
      {showFAQ && (
        <section className="bg-white pb-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              viewport={{ once: true }}
            >
              <FAQ
                items={get__pilot_faq_data(locale).map((item: PilotFAQItem) => {
                  let answer: string | React.ReactNode = item.answer;
                  
                  // Add book a call link to pilot package cost question
                  if (item.id === "2") {
                    answer = (
                      <span>
                        {item.answer}{" "}
                        <Link href="/book-a-demo/" className="text-blue-600 hover:underline">
                          Book a call to discuss further
                        </Link>
                        .
                      </span>
                    );
                  }
                  
                  // Add accreditations and Trust Center link to accessibility question
                  if (item.id === "4" && item.accreditations) {
                    answer = (
                      <div>
                        <div className="mb-4">{item.answer}</div>
                        <div className="space-y-3">
                          {item.accreditations.map((accreditation, index) => (
                            <div key={index} className="flex items-center gap-3">
                              <div className="flex items-center justify-center size-6 rounded-full bg-green-500">
                                <Check className="size-4 text-white stroke-3" />
                              </div>
                              <span>{accreditation.title}</span>
                            </div>
                          ))}
                        </div>
                        <div className="mt-4 text-black">
                          All key documents are available in our{" "}
                          <Link href="https://trust.studystash.com/" className="text-blue-600 hover:underline">
                            Trust Center
                          </Link>
                          .
                        </div>
                      </div>
                    );
                  }
                  
                  return {
                    id: item.id,
                    question: item.question,
                    answer: answer
                  };
                })}
                title="Frequently Asked Questions"
                className="max-w-6xl mx-auto"
              />
            </motion.div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section id="apply-now" className="bg-white py-16 border-t border-gray-200">
        <div className="mx-auto max-w-6xl px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            viewport={{ once: true }}
          >
            <div className="flex justify-center w-full">
              <CustomForm formId={application_form_id} />
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
